// ============================================================================
// GESTIMMONY — Page Baux (/app/baux)
// ============================================================================
// Gestion des contrats de location :
//   - Liste des baux avec bien, locataire, période, loyer + charges
//   - Création / édition via une modale (Dialog shadcn)
//   - Téléchargement du PDF du contrat (signatures incrustées)
//   - Signature électronique des 2 parties (bailleur + locataire) avec
//     horodatage — via le composant SignaturePad (canvas au doigt/souris)
//
// API consommées :
//   GET    /api/leases, /api/properties, /api/tenants  (chargement)
//   POST   /api/leases                                 (création)
//   PUT    /api/leases/{id}                            (édition)
//   DELETE /api/leases/{id}                            (suppression)
//   GET    /api/leases/{id}/pdf                        (PDF du bail)
//   POST   /api/leases/{id}/sign                       (signature)
// ============================================================================
import { useEffect, useState } from "react";
import { api, fmtEUR, fmtDate, LEASE_TYPES } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { planContext } from "@/lib/plan";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, FileText, Download, Pencil, PenLine, Check } from "lucide-react";
import { toast } from "sonner";              // notifications toast (haut-droite)
import SignaturePad from "@/components/app/SignaturePad";

// Gabarit de formulaire vide — réutilisé pour créer un bail et réinitialiser
// la modale après un enregistrement
const empty = {
  property_id: "", tenant_id: "", type: "vide",
  start_date: "", end_date: "",
  rent_amount: 0, charges_amount: 0, charges_mode: "provision", deposit_amount: 0, payment_day: 5,
  notes: "",
};

// Pastille indiquant le mode de gestion des charges du bail
function ChargesModeBadge({ mode }) {
  if (mode === "reel") {
    return (
      <Badge variant="outline" data-testid="lease-charges-mode-badge"
        className="border-amber-300 bg-amber-50 text-amber-800 text-[10px] font-medium">
        Charges au réel
      </Badge>
    );
  }
  return (
    <Badge variant="outline" data-testid="lease-charges-mode-badge"
      className="border-teal-200 bg-teal-50 text-teal-800 text-[10px] font-medium">
      Provision mensuelle
    </Badge>
  );
}


export default function LeasesPage() {
  const { user } = useAuth();
  const { canWrite, isDemo } = planContext(user);
  // --- États de la page ---
  const [items, setItems] = useState([]);        // liste des baux
  const [props, setProps] = useState([]);        // biens (pour le menu déroulant)
  const [tenants, setTenants] = useState([]);    // locataires (idem)
  const [open, setOpen] = useState(false);       // modale création/édition ouverte ?
  const [form, setForm] = useState(empty);       // contenu du formulaire
  const [editId, setEditId] = useState(null);    // null = création, sinon id du bail édité

  // Charge baux + biens + locataires en parallèle (les 3 listes sont
  // nécessaires pour remplir les menus déroulants de la modale)
  const load = async () => {
    const [l, p, t] = await Promise.all([
      api.get("/leases"), api.get("/properties"), api.get("/tenants"),
    ]);
    setItems(l.data); setProps(p.data); setTenants(t.data);
  };
  useEffect(() => { load(); }, []);   // une fois au montage de la page

  // --- Gestion de la signature électronique ---
  // État de la modale de signature : quel bail, quel rôle (bailleur/locataire),
  // et la signature existante éventuelle (pré-affichée dans le canvas)
  const [signState, setSignState] = useState({ open: false, leaseId: null, role: "landlord", initial: null });
  const openSign = (lease, role) => setSignState({
    open: true, leaseId: lease.id, role,
    initial: role === "landlord" ? lease.signature_landlord : lease.signature_tenant,
  });
  // Appelé par SignaturePad quand l'utilisateur valide son dessin
  const saveSignature = async (dataUrl) => {
    try {
      await api.post(`/leases/${signState.leaseId}/sign`, { role: signState.role, signature: dataUrl });
      toast.success("Signature enregistrée");
      load();   // recharge la liste pour afficher la date de signature
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
  };

  // Ouvre la modale en mode création (formulaire vide)
  const openNew = () => { if (!canWrite) return; setForm(empty); setEditId(null); setOpen(true); };
  // Ouvre la modale en mode édition (formulaire pré-rempli depuis le bail)
  // .slice(0, 10) tronque une date ISO complète ("2026-06-01T00:00:00...")
  // vers "2026-06-01" attendue par les <input type="date">
  const openEdit = (l) => {
    setForm({
      property_id: l.property_id,
      tenant_id: l.tenant_id,
      type: l.type,
      start_date: (l.start_date || "").slice(0, 10),
      end_date: (l.end_date || "").slice(0, 10),
      rent_amount: l.rent_amount,
      charges_amount: l.charges_amount,
      charges_mode: l.charges_mode || "provision",
      deposit_amount: l.deposit_amount ?? 0,
      payment_day: l.payment_day ?? 5,
      notes: l.notes || "",
    });
    setEditId(l.id); setOpen(true);
  };

  // Enregistre le bail : PUT si édition (editId présent), POST si création.
  // Les champs numériques sont forcés en Number() car les <input> renvoient
  // toujours des chaînes de caractères.
  const save = async () => {
    if (!canWrite) return;
    try {
      const payload = {
        ...form,
        rent_amount: Number(form.rent_amount || 0),
        charges_amount: form.charges_mode === "reel" ? 0 : Number(form.charges_amount || 0),
        charges_mode: form.charges_mode || "provision",
        deposit_amount: Number(form.deposit_amount || 0),
        payment_day: Number(form.payment_day || 5),
      };
      if (editId) {
        await api.put(`/leases/${editId}`, payload); toast.success("Bail mis à jour");
      } else {
        await api.post("/leases", payload); toast.success("Bail créé");
      }
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
  };

  // Suppression avec confirmation native du navigateur
  const remove = async (id) => {
    if (!canWrite) return;
    if (!window.confirm("Supprimer ce bail ?")) return;
    await api.delete(`/leases/${id}`); toast.success("Supprimé"); load();
  };

  // Télécharge le PDF du bail : la réponse arrive en blob (fichier binaire),
  // on crée une URL temporaire et on simule un clic sur un lien de
  // téléchargement, puis on libère la mémoire (revokeObjectURL).
  const downloadPdf = async (id) => {
    if (isDemo) return;
    try {
      const res = await api.get(`/leases/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `bail_${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur lors du téléchargement"); }
  };

  return (
    <div className="space-y-6" data-testid="leases-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Baux</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} contrat(s) de location.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="lease-new-btn" onClick={openNew} disabled={!canWrite} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90">
              <Plus className="h-4 w-4 mr-2" /> Nouveau bail
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editId ? "Modifier le bail" : "Créer un bail"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Bien</Label>
                <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                  <SelectTrigger data-testid="lease-property"><SelectValue placeholder="Sélectionner un bien" /></SelectTrigger>
                  <SelectContent>
                    {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.label} — {p.city}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Locataire</Label>
                <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                  <SelectTrigger data-testid="lease-tenant"><SelectValue placeholder="Sélectionner un locataire" /></SelectTrigger>
                  <SelectContent>
                    {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type de bail</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEASE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Jour de paiement</Label><Input type="number" min="1" max="31" value={form.payment_day} onChange={(e) => setForm({ ...form, payment_day: e.target.value })} /></div>
              <div><Label>Date de début</Label><Input type="date" data-testid="lease-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>Date de fin</Label><Input type="date" data-testid="lease-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              <div><Label>Loyer (€)</Label><Input type="number" data-testid="lease-rent" value={form.rent_amount} onChange={(e) => setForm({ ...form, rent_amount: e.target.value })} /></div>
              <div className="md:col-span-2">
                <Label>Gestion des charges</Label>
                <Select value={form.charges_mode} onValueChange={(v) => setForm({ ...form, charges_mode: v })}>
                  <SelectTrigger data-testid="lease-charges-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="provision">Provision mensuelle + régularisation annuelle</SelectItem>
                    <SelectItem value="reel">Au réel (tout à la régularisation annuelle)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-slate-500">
                  {form.charges_mode === "reel"
                    ? "Aucune charge appelée chaque mois ; les charges récupérables sont régularisées une fois par an."
                    : "Une provision est appelée chaque mois sur la quittance, puis régularisée annuellement."}
                </p>
              </div>
              {form.charges_mode !== "reel" && (
                <div data-testid="lease-charges-amount-wrap"><Label>Provision de charges mensuelle (€)</Label><Input type="number" data-testid="lease-charges" value={form.charges_amount} onChange={(e) => setForm({ ...form, charges_amount: e.target.value })} /></div>
              )}
              <div className="md:col-span-2"><Label>Dépôt de garantie (€)</Label><Input type="number" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Clauses particulières</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button data-testid="lease-save" onClick={save} className="bg-[hsl(var(--primary))]">{editId ? "Enregistrer" : "Créer"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 shadow-none overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="h-10 w-10 mx-auto text-slate-300" />
            <div className="mt-3 text-sm text-slate-500">Aucun bail actif.</div>
          </div>
        ) : (
          <>
          <Table className="hidden sm:table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Bien</TableHead>
                <TableHead>Locataire</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Période</TableHead>
                <TableHead>Loyer + charges</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((l) => (
                <TableRow key={l.id} data-testid={`lease-row-${l.id}`}>
                  <TableCell>
                    <div className="font-medium">{l.property_label}</div>
                    <div className="text-xs text-slate-500">{l.property_address}</div>
                  </TableCell>
                  <TableCell className="text-sm">{l.tenant_name}</TableCell>
                  <TableCell><Badge variant="outline" className="capitalize">{l.type}</Badge></TableCell>
                  <TableCell className="text-sm">{fmtDate(l.start_date)} → {fmtDate(l.end_date)}</TableCell>
                  <TableCell className="text-sm font-medium">
                    <div>{fmtEUR(Number(l.rent_amount) + Number(l.charges_amount))}</div>
                    <ChargesModeBadge mode={l.charges_mode} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" disabled={isDemo} onClick={() => downloadPdf(l.id)} data-testid={`lease-pdf-${l.id}`}>
                      <Download className="h-4 w-4 mr-1" /> PDF
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openSign(l, "landlord")} data-testid={`lease-sign-landlord-${l.id}`}
                      title={l.signature_landlord_at ? `Signé le ${l.signature_landlord_at.slice(0,10)}` : "Signer en tant que bailleur"}
                      className={l.signature_landlord ? "text-emerald-700" : ""}
                    >
                      {l.signature_landlord ? <Check className="h-4 w-4 mr-1" /> : <PenLine className="h-4 w-4 mr-1" />}
                      Bailleur
                      {l.signature_landlord_at && <span className="ml-1 text-[10px] text-emerald-600/80">{l.signature_landlord_at.slice(0,10)}</span>}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openSign(l, "tenant")} data-testid={`lease-sign-tenant-${l.id}`}
                      title={l.signature_tenant_at ? `Signé le ${l.signature_tenant_at.slice(0,10)}` : "Signature du locataire"}
                      className={l.signature_tenant ? "text-emerald-700" : ""}
                    >
                      {l.signature_tenant ? <Check className="h-4 w-4 mr-1" /> : <PenLine className="h-4 w-4 mr-1" />}
                      Locataire
                      {l.signature_tenant_at && <span className="ml-1 text-[10px] text-emerald-600/80">{l.signature_tenant_at.slice(0,10)}</span>}
                    </Button>
                    <Button variant="ghost" size="icon" disabled={!canWrite} onClick={() => openEdit(l)} data-testid={`lease-edit-${l.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" disabled={!canWrite} onClick={() => remove(l.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Vue mobile : cartes empilées */}
          <div className="sm:hidden divide-y divide-slate-100">
            {items.map((l) => (
              <div key={l.id} className="p-4" data-testid={`lease-card-${l.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{l.property_label}</div>
                    <div className="text-xs text-slate-500 truncate">{l.property_address}</div>
                  </div>
                  <Badge variant="outline" className="capitalize shrink-0">{l.type}</Badge>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-y-1 text-sm">
                  <div><span className="text-slate-500">Locataire : </span>{l.tenant_name}</div>
                  <div><span className="text-slate-500">Période : </span>{fmtDate(l.start_date)} → {fmtDate(l.end_date)}</div>
                  <div className="flex items-center gap-2"><span className="text-slate-500">Loyer + charges : </span><span className="font-medium">{fmtEUR(Number(l.rent_amount) + Number(l.charges_amount))}</span><ChargesModeBadge mode={l.charges_mode} /></div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" disabled={isDemo} onClick={() => downloadPdf(l.id)} data-testid={`lease-pdf-m-${l.id}`}><Download className="h-4 w-4 mr-1" /> PDF</Button>
                  <Button variant="outline" size="sm" onClick={() => openSign(l, "landlord")} data-testid={`lease-sign-landlord-m-${l.id}`} className={l.signature_landlord ? "text-emerald-700" : ""}>
                    {l.signature_landlord ? <Check className="h-4 w-4 mr-1" /> : <PenLine className="h-4 w-4 mr-1" />} Bailleur
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openSign(l, "tenant")} data-testid={`lease-sign-tenant-m-${l.id}`} className={l.signature_tenant ? "text-emerald-700" : ""}>
                    {l.signature_tenant ? <Check className="h-4 w-4 mr-1" /> : <PenLine className="h-4 w-4 mr-1" />} Locataire
                  </Button>
                  <Button variant="outline" size="sm" disabled={!canWrite} onClick={() => openEdit(l)} data-testid={`lease-edit-m-${l.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="outline" size="sm" disabled={!canWrite} onClick={() => remove(l.id)} data-testid={`lease-delete-m-${l.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </Card>

      <SignaturePad
        open={signState.open}
        onOpenChange={(v) => setSignState({ ...signState, open: v })}
        onSave={saveSignature}
        title={signState.role === "landlord" ? "Signature du bailleur" : "Signature du locataire"}
        initialSignature={signState.initial}
      />
    </div>
  );
}
